import { Component, lazy, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { decrypt, storedKey } from "./crypto";
import type { ProtectedPageName } from "./index";
import Icon from "@/components/base/Icon";

type ProtectedModule = typeof import("./index");
let modulePromise: Promise<ProtectedModule> | null = null;

/** Decrypts src/protected/sealed.ts (made by scripts/seal-nda.mjs) and runs
    it. The sealed code gets React, the router and the shared components from
    the site itself, through globalThis.__ndaDeps. */
async function loadSealed(): Promise<ProtectedModule> {
  const [{ default: sealedText }, { deps }] = await Promise.all([import("./sealed"), import("./deps")]);
  const sealed = Uint8Array.from(atob(sealedText), (ch) => ch.charCodeAt(0));
  const plain = await decrypt(await storedKey(), sealed);
  Object.assign(globalThis, { __ndaDeps: deps, __ndaEnv: import.meta.env });
  const url = URL.createObjectURL(new Blob([plain], { type: "text/javascript" }));
  try {
    return await import(/* @vite-ignore */ url);
  } finally {
    URL.revokeObjectURL(url);
  }
}

function loadProtected(): Promise<ProtectedModule> {
  if (import.meta.env.DEV) return import("./index"); // local dev / editor preview only
  if (!modulePromise) {
    modulePromise = loadSealed().catch((e) => {
      modulePromise = null;
      throw e;
    });
  }
  return modulePromise;
}

export function protectedPage(name: ProtectedPageName) {
  return lazy(() => loadProtected().then((m) => ({ default: m.pages[name] })));
}

export class ProtectedLoadBoundary extends Component<{ children: ReactNode }, { failed: boolean; reason: string }> {
  state = { failed: false, reason: "" };
  static getDerivedStateFromError(error: unknown) {
    return { failed: true, reason: error instanceof Error ? `${error.name}: ${error.message}` : String(error) };
  }
  componentDidCatch(error: unknown) {
    console.error("[protected pages]", error);
  }
  render() {
    if (!this.state.failed) return this.props.children;
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center px-4">
        <div className="max-w-md text-center" role="alert">
          <h1 className="text-2xl font-light text-white mb-3">This case study could not be loaded</h1>
          <p className="text-gray-300 leading-relaxed mb-6">
            Please reload the page. If it happens again, email{" "}
            <a className="link" href="mailto:info.lorenzobernardi@proton.me">info.lorenzobernardi@proton.me</a>.
          </p>
          <Link to="/" className="btn btn-secondary px-6 py-3 text-base mx-auto">
            <Icon name="arrow-left" aria-hidden="true" /> Back to Portfolio
          </Link>
          {/* Technical detail, handy when reporting the problem. Never contains page content. */}
          {this.state.reason && (
            <p className="font-mono text-gray-400 text-sm mt-8 break-words">{this.state.reason}</p>
          )}
        </div>
      </div>
    );
  }
}
